<script setup lang="ts">
import { useScreenOrientation } from '@vueuse/core';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { PWAPrompt } from 'vue-ios-pwa-prompt';
import { useDeviceDetection } from './composables/useDeviceDetection';
import { isIPadDevice } from './utils';

import { useRoute } from 'vue-router';
import MainNav from './components/MainNav.vue';
import Toast from './components/Toast.vue';
import { useMidi } from './composables/useMidi';
import { Capacitor } from '@capacitor/core';
import { useSettings } from './composables/useSettings';
import { useConvexAuth } from './composables/useConvexAuth';
import { useSingleDeviceSession } from './composables/useSingleDeviceSession';
import { useAppUpdate } from './composables/useAppUpdate';
import UpdateRequiredOverlay from './components/UpdateRequiredOverlay.vue';
import AudioWaveform from './components/AudioWaveform.vue';

// Initialize Convex auth sync with Supabase
useConvexAuth();
// Enforce one active device per account — signs out if another device signs in
useSingleDeviceSession();
// Self-hosted OTA live updates + native "Update required" gate (native-only)
const { mustUpdate, storeUrl } = useAppUpdate();

const dummyAudioFile = ref<File>(new File([], ''));

const route = useRoute();
const midi = useMidi();
const screenOrientation = useScreenOrientation();

const settings = useSettings();

screenOrientation.lockOrientation('landscape-primary').catch(() => {
  console.log("lock orientation not supported");
});

const { isMobileDevice } = useDeviceDetection();
const previousPosition = ref(history.state?.position || 0)
const transitionName = ref('fade')
let mediaQuery: MediaQueryList;
const THEME_COLOR_LIGHT = '#F8FAFC';
const THEME_COLOR_DARK = '#000D24';

function setThemeColorMeta(theme: 'light' | 'dark') {
  const color = theme === 'dark' ? THEME_COLOR_DARK : THEME_COLOR_LIGHT;
  const meta = document.head.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
  if (meta) {
    meta.setAttribute('content', color);
  }
  document.documentElement.style.backgroundColor = color;
}

const handleChange = () => {
  if (settings.theme.value === 'system') {
    if (mediaQuery.matches) {
      document.documentElement.classList.add('dark');
      setThemeColorMeta('dark');
    } else {
      document.documentElement.classList.remove('dark');
      setThemeColorMeta('light');
    }
  }
};

onMounted(async () => {
  midi.enableMidi();

  // Watch for theme changes
  watch(settings.currentTheme, (theme) => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      setThemeColorMeta('dark');
    } else {
      document.documentElement.classList.remove('dark');
      setThemeColorMeta('light');
    }
  }, { immediate: true });

  // Watch for system theme changes
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  mediaQuery.addEventListener('change', handleChange);
});

onUnmounted(() => {
  mediaQuery.removeEventListener('change', handleChange);
});

async function deviceOrientation() {
  const body = document.body;
  const isIPadModel = await isIPadDevice();

  const angle = screenOrientation.angle.value;
  const normalizedAngle = isIPadModel ? (angle - 90 + 360) % 360 : angle;

  switch(normalizedAngle) {
    case 0: {
      body.classList.remove(...body.classList);
      body.classList.add('rotation90');
      // special handling for mobile iOS viewport height changes when device orientation changes
      await new Promise(resolve => setTimeout(resolve, 0));
      const bodyRect = body.getBoundingClientRect()
      const currentScreenWidthOffset = parseFloat(body.style.getPropertyValue('--screen-width-offset')) || 0;
      body.style.setProperty('--screen-width-offset', `${currentScreenWidthOffset + bodyRect.top}px`);
      break;
    }
    case 180:
      body.classList.remove(...body.classList);
      body.classList.add('rotation-90');
      break;
    default:
      body.classList.remove(...body.classList);
      body.classList.add('landscape');
      break;
  }
}

watch(screenOrientation.orientation, () => {
  if (isMobileDevice.value) {
    deviceOrientation();
  }
}, { immediate: true })

watch(route, () => {
  const currentPosition = history.state?.position || 0
  transitionName.value = currentPosition <= previousPosition.value ? 'slide-right' : 'slide-left'
  previousPosition.value = currentPosition
})
</script>

<template>
  <UApp>
    <MainNav />
    <!-- TODO: perhaps not initialize a dummy audio waveform here but 
     just register the use audio context composable, or create a plugin -->
    <AudioWaveform
      class="hidden"
      :audio-file="dummyAudioFile"
    />
    <div
      id="main"
      class="mx-auto absolute inset-0"
      :data-page="route.name"
      :class="{ 'pt-16': !isMobileDevice, 'py-0': isMobileDevice}"
    >
      <router-view v-slot="{ Component, route: routeLocation }">
        <transition :name="routeLocation.meta.transition as string ?? transitionName ?? 'fade'">
          <!-- Only list-bearing views are cached, so drilling into one and coming
               back keeps its filters and sort. AdminView rather than its children:
               `include` matches only the vnode keep-alive wraps, and the admin
               lists live in its nested RouterView. Detail views are deliberately
               absent — PieceGameView holds an OSMD instance, a game engine and
               decoded audio that must be released on exit. `max` bounds the rest,
               since the key is the path and every edit URL adds an entry. -->
          <keep-alive
            :include="['PiecesView', 'AdminView', 'ClassroomLobbyView']"
            :max="10"
          >
            <component
              :is="Component"
              :key="routeLocation.path"
              class="mx-auto absolute inset-0"
              :class="{ 'pt-16': !isMobileDevice, 'py-0': isMobileDevice}"
            />
          </keep-alive>
        </transition>
      </router-view>
    </div>
    
    <PWAPrompt
      v-if="!Capacitor.isNativePlatform()"
      app-icon-path="/favicon.svg"
    />
    <Toast />
    <UpdateRequiredOverlay
      v-if="mustUpdate"
      :store-url="storeUrl"
    />
  </UApp>
</template>